import React, { useState, useEffect } from 'react';
import DestinationModal from '../../components/destination-modal';
import { AnimatePresence, motion } from 'framer-motion';

const mockPlaces = [
  {
    id: 1,
    title: "Lalbagh Botanical Garden",
    image: "https://plus.unsplash.com/premium_photo-1697729606148-42a74a5bad37?q=80&w=2670&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    description: "A lush green paradise in the heart of Bangalore.",
    longDescription: "One of the largest botanical gardens in India, Lalbagh houses India's largest collection of tropical plants, a stunning glass house, and a picturesque lake. The garden is particularly famous for its bi-annual flower shows.",
    bestTime: "Early Morning & Evening",
    stayDuration: "2-3 hours",
    idealFor: "Nature Lovers & Photography Enthusiasts",
    highlights: [
      "Glass House",
      "Flower Shows",
      "Lake View",
      "Historical Trees"
    ]
  },
  {
    id: 2,
    title: "Cubbon Park",
    image: "https://plus.unsplash.com/premium_photo-1697729606148-42a74a5bad37?q=80&w=2670&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    description: "A green oasis in the midst of Bangalore's urban landscape.",
    longDescription: "Spread over 300 acres, Cubbon Park is a lung space that offers a perfect escape from the city's hustle. With its diverse flora, historical statues, and serene walking paths, it's a favorite among locals and tourists alike.",
    bestTime: "Early Morning & Evening",
    stayDuration: "1-2 hours",
    idealFor: "Nature Enthusiasts & Joggers",
    highlights: [
      "State Library",
      "Bangalore Museum",
      "Children's Park",
      "Ancient Trees"
    ]
  },
  {
    id: 3,
    title: "UB City Mall",
    image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?q=80&w=2000&auto=format&fit=crop",
    description: "Luxury shopping destination in the heart of the city.",
    longDescription: "UB City is Bangalore's first luxury mall, hosting high-end brands, fine dining restaurants, and a rooftop bar offering panoramic city views. The architecture blends modern design with classic elements.",
    bestTime: "Afternoon & Evening",
    stayDuration: "3-4 hours",
    idealFor: "Shopping & Fine Dining",
    highlights: [
      "Luxury Brands",
      "Sky Bar",
      "Art Gallery",
      "Fine Dining"
    ]
  }
];

const SearchPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isVerticalLayout, setIsVerticalLayout] = useState(false);

  const suggestions = [
    'Parks & Gardens',
    'Temples',
    'Shopping',
    'Nature Spots',
    'Wildlife',
    'Historical Places',
    'Tourist Attractions',
    'Sunrise Views',
    'Food & Dining',
    'Photography Spots',
    'Weekend Getaways',
    'Family Fun'
  ];

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      const isToggleButton = e.target.closest('.layout-toggle');
      if (!e.target.closest('.search-container') && !isToggleButton) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Animation variants for click effects
  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3 }
    },
    tap1: { 
      scale: 0.95,
      rotate: [0, -2, 2, -2, 0],
      boxShadow: "0px 5px 15px rgba(0,0,0,0.2)",
      filter: "brightness(0.95)",
      transition: { 
        type: "spring", 
        stiffness: 400,
        damping: 10
      }
    },
    tap2: {
      scale: 0.9,
      y: [0, -10, 0],
      x: [0, 5, -5, 5, 0],
      filter: "brightness(1.1)",
      transition: { 
        duration: 0.4,
        ease: "easeInOut"
      }
    },
    tap3: {
      scale: [1, 0.9, 1.1, 0.95, 1],
      rotate: [0, 0, -3, 3, 0],
      filter: "contrast(1.1)",
      transition: { 
        duration: 0.5,
        times: [0, 0.2, 0.4, 0.6, 1]
      }
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.toLowerCase().replace(' & ', ' ').replace(' spots', ''));
    setShowSuggestions(false);
    setShowResults(true);
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowResults(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
  };

  const filteredPlaces = mockPlaces.filter(place => {
    const searchLower = searchQuery.toLowerCase();
    const searchTerms = searchLower.split(' ').filter(term => term.length > 0);
    
    if (searchTerms.length === 0 || searchLower === 'bangalore') {
      return true;
    }

    // Special search cases
    const searchCases = {
      'parks': ['park', 'garden'],
      'gardens': ['garden', 'botanical'],
      'temples': ['temple', 'spiritual', 'religious'],
      'shopping': ['mall', 'shopping', 'boutique'],
      'nature': ['park', 'garden', 'hills', 'botanical'],
      'wildlife': ['safari', 'animal', 'wildlife', 'zoo'],
      'sunrise': ['sunrise', 'morning', 'hills'],
      'food': ['restaurant', 'dining', 'food'],
      'photography': ['view', 'scenic', 'photography'],
      'weekend': ['getaway', 'retreat', 'hills'],
      'family': ['park', 'family', 'kids', 'children']
    };

    // Check if search matches any special cases
    for (const [category, keywords] of Object.entries(searchCases)) {
      if (searchLower.includes(category)) {
        return keywords.some(keyword => 
          place.title.toLowerCase().includes(keyword) ||
          place.description.toLowerCase().includes(keyword) ||
          place.longDescription.toLowerCase().includes(keyword) ||
          place.highlights.some(highlight => highlight.toLowerCase().includes(keyword))
        );
      }
    }

    // Default search through all text
    return searchTerms.every(term => 
      place.title.toLowerCase().includes(term) ||
      place.description.toLowerCase().includes(term) ||
      place.longDescription.toLowerCase().includes(term) ||
      place.highlights.some(highlight => highlight.toLowerCase().includes(term))
    );
  });

  const displayPlaces = searchQuery ? filteredPlaces : mockPlaces;
  const noResults = searchQuery && filteredPlaces.length === 0;

  return (
    <div className="min-h-screen bg-custom-blue/5 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="relative mb-24">
          <div className="absolute -left-4 top-1/2 -translate-y-1/2 w-1 h-24 bg-custom-blue"></div>
          <h1 className="text-7xl font-bold text-custom-blue tracking-tight leading-none mb-4">
            Explore<br />Bangalore
          </h1>
          <p className="text-xl text-custom-blue/50 tracking-wide uppercase ml-1">Discover the city's hidden gems</p>
        </div>

        {/* Search Section */}
        <div className="flex justify-center mb-16">
          <form onSubmit={handleSubmit} className="w-full max-w-2xl">
            <div className="relative search-container">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearch}
                  onFocus={() => setShowSuggestions(true)}
                  placeholder="Search places in Bangalore..."
                  className="w-full px-0 py-6 text-3xl border-0 border-b-[3px] border-custom-blue bg-transparent text-custom-blue placeholder-custom-blue/20 outline-none hover:border-custom-blue focus:border-custom-blue transition-all duration-200 font-light"
                />
                <div className="absolute right-0 top-1/2 -translate-y-1/2">
                  <button
                    type="submit"
                    className="flex items-center justify-center w-14 h-14 bg-custom-blue text-white hover:bg-custom-blue/90 transition-all duration-200 hover:rotate-90"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                    </svg>
                  </button>
                </div>
              </div>
              <AnimatePresence>
                {showSuggestions && searchQuery.length === 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute left-0 right-0 mt-4 py-6 bg-custom-blue/5 border-l-[6px] border-custom-blue shadow-2xl z-20 max-h-[calc(100vh-250px)] overflow-y-auto overflow-x-hidden"
                  >
                    <div className="flex justify-between items-center px-12 py-3">
                      <div className="text-sm uppercase tracking-[0.2em] text-custom-blue/40 font-bold">
                        Popular searches
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsVerticalLayout(!isVerticalLayout);
                        }}
                        className="flex items-center gap-2 text-custom-blue hover:text-custom-blue/80 transition-colors layout-toggle"
                      >
                        <span className="text-sm">{isVerticalLayout ? 'Horizontal' : 'Vertical'} Layout</span>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d={isVerticalLayout ? "M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" : "M3 4.5h17M3 12h17M3 19.5h17"} />
                        </svg>
                      </button>
                    </div>
                    <div className={`${isVerticalLayout ? 'flex flex-col' : 'flex flex-wrap'}`}>
                      {suggestions.map((suggestion, index) => (
                        <motion.button
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className={`text-left px-12 py-4 text-2xl text-custom-blue hover:bg-custom-blue/10 cursor-pointer transition-all duration-200 font-light ${isVerticalLayout ? 'w-full' : 'w-1/2'}`}
                          onClick={() => handleSuggestionClick(suggestion)}
                        >
                          {suggestion}
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </form>
        </div>

        {/* Results Section */}
        {showResults && (
          <div className="relative w-full pb-12">
            {noResults ? (
              <motion.div 
                className="w-full text-center py-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <h3 className="text-2_5xl font-bold text-custom-blue mb-4">
                  No places found
                </h3>
                <p className="text-1_5xl text-custom-blue/80 mb-6">
                  Try different keywords or explore our suggested destinations
                </p>
                <div className="text-1_5xl text-custom-blue/70 mb-4">
                  Popular Categories:
                </div>
                <div className="flex flex-wrap gap-3 justify-center max-w-3xl mx-auto">
                  {suggestions.map((suggestion, index) => (
                    <motion.button
                      key={suggestion}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => setSearchQuery(suggestion.toLowerCase())}
                      className="px-4 py-2 text-1_5xl bg-white text-custom-blue border-2 border-custom-blue rounded-l-full rounded-r-full hover:bg-custom-blue hover:text-custom-white transition-all duration-200 hover:scale-105"
                    >
                      {suggestion}
                    </motion.button>
                  ))}
                </div>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="mt-8 text-1_5xl text-custom-blue/60"
                >
                  Try searching by category or specific location
                </motion.div>
              </motion.div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-6">
                {displayPlaces.map((place, index) => (
                  <motion.div
                    key={`${place.id}-${index}`}
                    className="bg-white rounded-custom shadow-lg overflow-hidden transform cursor-pointer"
                    onClick={() => setSelectedDestination(place)}
                    variants={cardVariants}
                    initial="hidden"
                    animate="visible"
                    whileHover={{ 
                      scale: 1.03,
                      rotate: 1,
                      y: -8,
                      boxShadow: "0px 10px 20px rgba(0,0,0,0.15)",
                      transition: { 
                        duration: 0.15,
                        ease: "easeOut"
                      }
                    }}
                    whileTap={cardVariants[`tap${(index % 3) + 1}`]}
                    layout
                  >
                    <div className="h-64 overflow-hidden">
                      <img
                        src={place.image}
                        alt={`Travel destination: ${place.title}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-2_5xl font-bold text-custom-blue mb-2">
                        {place.title}
                      </h3>
                      <p className="text-1_5xl text-custom-blue/80">
                        {place.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Destination Modal with Animation */}
        <AnimatePresence mode="wait">
          {selectedDestination && (
            <>
              {/* Overlay */}
              <motion.div
                key="overlay"
                initial={{ opacity: 0 }}
                animate={{ 
                  opacity: 0.7,
                  transition: { duration: 0.5, ease: "easeOut" } // for overlay
                }}
                exit={{ 
                  opacity: 0,
                  transition: { duration: 0.5, ease: "easeIn" } // for modal
                }}
                className="fixed inset-0 bg-black/45 backdrop-blur-sm z-40"
                onClick={() => setSelectedDestination(null)}
              />
              
              {/* Modal */}
              <motion.div
                key="modal"
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ 
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    duration: 0.5,
                    ease: "easeOut"
                  }
                }}
                exit={{ 
                  opacity: 0,
                  y: 20,
                  scale: 0.95,
                  transition: { 
                    duration: 0.5,
                    ease: "easeIn"
                  }
                }}
                className="fixed inset-0 z-50 flex items-center justify-center"
              >
                <motion.div
                  initial={{ opacity: 0, rotateX: -15 }}
                  animate={{ 
                    opacity: 1, 
                    rotateX: 0,
                    transition: { duration: 0.5, ease: "easeOut" }
                  }}
                  exit={{
                    opacity: 0,
                    rotateX: 15,
                    transition: { duration: 0.5, ease: "easeIn" }
                  }}
                  className="w-full max-w-4xl mx-4"
                >
                  <DestinationModal
                    destination={selectedDestination}
                    onClose={() => setSelectedDestination(null)}
                  />
                </motion.div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SearchPage;
